///
/// EarthBrowser
///
/// Created by Matt Giger on 10/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The sun: a camera-facing billboard at the sun's true geocentric position
/// drawing a disc with a soft glare (sun.glsl). Drawn before the globe with
/// the depth test off, so terrain and the limb occlude it naturally, and
/// tinted per frame by a small extinction model so it reddens and dims as
/// it nears the horizon seen from the camera's altitude.
///

import { cSunRadiusKm } from "@/earthbrowser/utils/astronomy"
import { wgs } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { trig } from "@stovokor/math/math"
import { type matrix } from "@stovokor/math/matrix"
import { dot, vec2, vec3 } from "@stovokor/math/vector"
import { BlendFactor } from "@stovokor/shader/GLConstants"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type SkyEphemeris } from "./Sky"
import { BillboardQuad, cApparentSizeScale, cSkyOrderSun, pixelsPerRadian, skyViewProjection } from "./SkyGeometry"

// Glare extent as a multiple of the disc radius
const cGlareRadiusScale = 7
// Sunlight before any atmosphere: slightly warm white
const cSunColor = new vec3(1.0, 0.97, 0.9)

// Extinction model. Zenith optical depth of a clear atmosphere in the
// visual band, the exponential scale height of the air, the air mass of a
// horizontal path at sea level, and how much faster the blue end is
// scattered out than the red end (the hue of the setting sun)
const cZenithOpticalDepth = 0.1
const cScaleHeightKm = 8.5
const cHorizontalAirMass = 38
const cExtinctionSpectrum = new vec3(0.45, 1.0, 1.9)

export interface SunAppearance {
	/** Sine of the sun's elevation above the camera's local horizontal */
	sinElevation: number
	/** Optical depth along the line of sight to the sun */
	opticalDepth: number
	/** Per-channel transmission applied to the sun's color */
	tint: vec3
	discRadiusPx: number
}

export class Sun extends NodeBase {
	private sunRenderer: SunRenderer

	constructor(context: StovokorContext, ephemeris: SkyEphemeris) {
		super()
		this.renderOrder = cSkyOrderSun
		this.sunRenderer = new SunRenderer(context, new BillboardQuad(context), ephemeris)
		this.renderer = this.sunRenderer
	}

	public debugAppearance(): SunAppearance | undefined {
		return this.sunRenderer.appearance
	}
}

/**
 * Transmission of sunlight to the camera through a clear atmosphere.
 * Above the local horizontal the path is the usual air mass of the sun's
 * elevation (Kasten-Young), thinned by the camera's own altitude. Below it
 * the ray grazes the atmosphere at a perigee under the camera: one leg in
 * from space to the perigee and one back up to the camera, each the
 * horizontal air mass scaled by the density at the perigee altitude
 */
export function sunTransmission(cameraPosition: vec3, sunDirection: vec3): { sinElevation: number, opticalDepth: number, tint: vec3 } {
	const radius = cameraPosition.length
	const up = cameraPosition.multiply(1 / radius)
	const altitudeKm = Math.max(radius - wgs.a, 0)
	const sinElevation = dot(up, sunDirection)
	const cameraDensity = Math.exp(-altitudeKm / cScaleHeightKm)

	let opticalDepth: number
	if (sinElevation >= 0) {
		const elevationDeg = Math.asin(Math.min(sinElevation, 1)) * trig.deg
		const airMass = 1 / (sinElevation + 0.15 * Math.pow(elevationDeg + 3.885, -1.253))
		opticalDepth = cZenithOpticalDepth * airMass * cameraDensity
	} else {
		const perigee = radius * Math.sqrt(Math.max(1 - sinElevation * sinElevation, 0))
		const perigeeDensity = Math.exp(-Math.max(perigee - wgs.a, 0) / cScaleHeightKm)
		opticalDepth = cZenithOpticalDepth * cHorizontalAirMass * (2 * perigeeDensity - cameraDensity)
	}

	const tint = new vec3(
		Math.exp(-opticalDepth * cExtinctionSpectrum.x),
		Math.exp(-opticalDepth * cExtinctionSpectrum.y),
		Math.exp(-opticalDepth * cExtinctionSpectrum.z)
	)
	return { sinElevation, opticalDepth, tint }
}

class SunRenderer extends Renderer {
	public appearance?: SunAppearance

	private modelViewProjection = new UniformDynamic<matrix>()
	private sunPosition = new UniformDynamic<vec3>()
	private viewport = new UniformDynamic<vec2>()
	private discRadius = new UniformDynamic<number>()
	private quadRadius = new UniformDynamic<number>()
	private sunColor = new UniformDynamic<vec3>()
	private ephemeris: SkyEphemeris

	constructor(context: StovokorContext, geometry: BillboardQuad, ephemeris: SkyEphemeris) {
		super(context, new ShaderSourceDefinition("sun"), geometry)
		this.ephemeris = ephemeris
		// Premultiplied: the shader writes alpha 1 inside the disc, 0 in the glare
		this.drawState.depthTest = false
		this.drawState.depthWrite = false
		this.drawState.blend = [BlendFactor.One, BlendFactor.OneMinusSrcAlpha]
	}

	protected stateUpdate(state: RenderState): void {
		const e = this.ephemeris
		this.modelViewProjection.value = skyViewProjection(state)
		this.sunPosition.value = e.sunPosition
		this.viewport.value = new vec2(state.width, state.height)

		const distanceKm = e.sunPosition.subtract(state.camera.position).length
		const discRadiusPx = Math.atan(cSunRadiusKm / distanceKm) * cApparentSizeScale * pixelsPerRadian(state)
		this.discRadius.value = discRadiusPx
		this.quadRadius.value = discRadiusPx * cGlareRadiusScale

		const transmission = sunTransmission(state.camera.position, e.sunDirection)
		this.sunColor.value = new vec3(
			cSunColor.x * transmission.tint.x,
			cSunColor.y * transmission.tint.y,
			cSunColor.z * transmission.tint.z
		)
		this.appearance = { ...transmission, discRadiusPx }
	}
}
